import { useTranslation } from 'react-i18next'
import { ROUTES } from '@/lib/routes'
import { AlertTriangle, Sparkles } from 'lucide-react'
import { AppLink } from '@/components/ui/app-link'
import { useAuth } from '@/lib/AuthContext'
import { cn } from '@/lib/utils'

/** Where the community's Memberfy plan is changed (the plan subscription lives in Billing, F-89). */
export const PLAN_CHANGE_PATH = ROUTES.accountBilling

/** "Ver planos", only for who can change the plan (owner and admin); nothing for the others. */
export function PlanUpgradeLink({ className }: { className?: string }) {
  const { t } = useTranslation('entitlements')
  const { hasRole } = useAuth()
  if (!hasRole(['owner', 'admin'])) return null
  return (
    <AppLink to={PLAN_CHANGE_PATH} className={cn('font-medium underline underline-offset-2', className)} data-testid="plan-upgrade-link">
      {t('upgrade.seePlans')}
    </AppLink>
  )
}

interface PlanLimitNoticeProps {
  /** What happened, in the reader's words (often the API's own translated refusal). */
  message: string
  /** `near` warns (amber), `over` says the quota ran out (red). */
  level?: 'near' | 'over'
  /** False when there is nothing a plan change would solve. */
  showUpgrade?: boolean
  /** A sentence for who can change the plan ("Mude de plano para ter mais."), before the link; others never see it. */
  upgradeHint?: string
  className?: string
  testId?: string
}

/**
 * F-34: a plan quota near or past its limit, with the way out: "Ver planos" for
 * owner and admin. One block, so the reason and the action read together.
 */
export function PlanLimitNotice({ message, level = 'over', showUpgrade = true, upgradeHint, className, testId }: PlanLimitNoticeProps) {
  const { hasRole } = useAuth()
  const canChangePlan = showUpgrade && hasRole(['owner', 'admin'])
  const Icon = level === 'over' ? AlertTriangle : Sparkles
  return (
    <div
      role={level === 'over' ? 'alert' : 'status'}
      data-testid={testId}
      className={cn(
        'flex items-start gap-2 rounded-lg p-3 text-sm',
        level === 'over' ? 'bg-red-50 text-red-800' : 'bg-amber-50 text-amber-900',
        className
      )}
    >
      <Icon className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1">
        {message}
        {canChangePlan && upgradeHint && <> {upgradeHint}</>}
        {canChangePlan && <> <PlanUpgradeLink /></>}
      </p>
    </div>
  )
}
